// src/services/walletCoreService.js - Modern wallet service using @coinley/wallet-connect-core
import { 
  createQuickConfig, 
  CoinleyWalletProvider,
  ConnectButton,
  WalletModal,
  useWallet,
  useWalletConnect,
  useWalletSign,
  useWalletSignTypedData,
  useNetworkSwitch,
  useTokenBalance,
  useWalletTransaction,
  useWalletModal,
  useWalletDetection
} from '@coinley/wallet-connect-core';

// Wallet Types (maintaining backward compatibility)
export const WALLET_TYPES = {
  METAMASK: 'metamask',
  PHANTOM: 'phantom',
  TRONLINK: 'tronlink',
  TRUST_WALLET: 'trustwallet',
  LUTE: 'lute',
  COINBASE_WALLET: 'coinbaseWallet',
  WALLETCONNECT: 'walletConnect'
};

// Network Types (maintaining backward compatibility)
export const NETWORK_TYPES = {
  ETHEREUM: 'ethereum',
  BSC: 'bsc',
  TRON: 'tron',
  ALGORAND: 'algorand',
  SOLANA: 'solana',
  POLYGON: 'polygon',
  ARBITRUM: 'arbitrum',
  OPTIMISM: 'optimism',
  AVALANCHE: 'avalanche',
  BASE: 'base'
};

// Enhanced Network Configuration with wallet-connect-core supported networks
export const NETWORK_CONFIG = {
  [NETWORK_TYPES.ETHEREUM]: {
    name: 'Ethereum Mainnet',
    chainId: 1,
    symbol: 'ETH',
    decimals: 18,
    rpcUrl: 'https://eth.llamarpc.com',
    blockExplorerUrl: 'https://etherscan.io'
  },
  [NETWORK_TYPES.POLYGON]: {
    name: 'Polygon',
    chainId: 137,
    symbol: 'MATIC',
    decimals: 18,
    rpcUrl: 'https://polygon.llamarpc.com',
    blockExplorerUrl: 'https://polygonscan.com'
  },
  [NETWORK_TYPES.BSC]: {
    name: 'BNB Smart Chain',
    chainId: 56,
    symbol: 'BNB',
    decimals: 18,
    rpcUrl: 'https://bsc.llamarpc.com',
    blockExplorerUrl: 'https://bscscan.com'
  },
  [NETWORK_TYPES.ARBITRUM]: {
    name: 'Arbitrum One',
    chainId: 42161,
    symbol: 'ARB',
    decimals: 18,
    rpcUrl: 'https://arbitrum.llamarpc.com',
    blockExplorerUrl: 'https://arbiscan.io'
  },
  [NETWORK_TYPES.OPTIMISM]: {
    name: 'Optimism',
    chainId: 10,
    symbol: 'OP',
    decimals: 18,
    rpcUrl: 'https://optimism.llamarpc.com',
    blockExplorerUrl: 'https://optimistic.etherscan.io'
  },
  [NETWORK_TYPES.AVALANCHE]: {
    name: 'Avalanche',
    chainId: 43114,
    symbol: 'AVAX',
    decimals: 18,
    rpcUrl: 'https://avalanche.llamarpc.com',
    blockExplorerUrl: 'https://snowtrace.io'
  },
  [NETWORK_TYPES.BASE]: {
    name: 'Base',
    chainId: 8453,
    symbol: 'ETH',
    decimals: 18,
    rpcUrl: 'https://base.llamarpc.com',
    blockExplorerUrl: 'https://basescan.org'
  },
  // Non-EVM networks (for backward compatibility)
  [NETWORK_TYPES.TRON]: {
    name: 'TRON Mainnet',
    symbol: 'TRX',
    decimals: 6,
    rpcUrl: 'https://api.trongrid.io',
    blockExplorerUrl: 'https://tronscan.org'
  },
  [NETWORK_TYPES.ALGORAND]: {
    name: 'Algorand Mainnet',
    symbol: 'ALGO',
    decimals: 6,
    rpcUrl: 'https://mainnet-api.algonode.cloud',
    blockExplorerUrl: 'https://algoexplorer.io'
  },
  [NETWORK_TYPES.SOLANA]: {
    name: 'Solana Mainnet',
    symbol: 'SOL',
    decimals: 9,
    rpcUrl: 'https://rpc.ankr.com/solana',
    blockExplorerUrl: 'https://explorer.solana.com'
  }
};

// Token configurations with multi-network support
export const TOKEN_CONFIG = {
  USDT: {
    ethereum: {
      contractAddress: '0xdAC17F958D2ee523a2206206994597C13D831ec7',
      decimals: 6,
      symbol: 'USDT'
    },
    polygon: {
      contractAddress: '0xc2132D05D31c914a87C6611C10748AEb04B58e8F',
      decimals: 6,
      symbol: 'USDT'
    },
    bsc: {
      contractAddress: '0x55d398326f99059fF775485246999027B3197955',
      decimals: 18,
      symbol: 'USDT'
    },
    arbitrum: {
      contractAddress: '0xFd086bC7CD5C481DCC9C85ebE478A1C0b69FCbb9',
      decimals: 6,
      symbol: 'USDT'
    },
    optimism: {
      contractAddress: '0x94b008aA00579c1307B0EF2c499aD98a8ce58e58',
      decimals: 6,
      symbol: 'USDT'
    },
    avalanche: {
      contractAddress: '0x9702230A8Ea53601f5cD2dc00fDBc13d4dF4A8c7',
      decimals: 6,
      symbol: 'USDT'
    },
    base: {
      contractAddress: '0xfde4C96c8593536E31F229EA8f37b2ADa2699bb2',
      decimals: 6,
      symbol: 'USDT'
    },
    // Non-EVM networks
    tron: {
      contractAddress: 'TR7NHqjeKQxGTCi8q8ZY4pL8otSzgjLj6t',
      decimals: 6,
      symbol: 'USDT'
    },
    algorand: {
      assetId: 312769,
      decimals: 6,
      symbol: 'USDT'
    },
    solana: {
      contractAddress: 'Es9vMFrzaCERmJfrF4H2FYD4KCoNkY11McCe8BenwNYB',
      decimals: 6,
      symbol: 'USDT'
    }
  },
  USDC: {
    ethereum: {
      contractAddress: '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48',
      decimals: 6,
      symbol: 'USDC'
    },
    polygon: {
      contractAddress: '0x2791Bca1f2de4661ED88A30C99A7a9449Aa84174',
      decimals: 6,
      symbol: 'USDC'
    },
    bsc: {
      contractAddress: '0x8AC76a51cc950d9822D68b83fE1Ad97B32Cd580d',
      decimals: 18,
      symbol: 'USDC'
    },
    arbitrum: {
      contractAddress: '0xFF970A61A04b1cA14834A43f5dE4533eBDDB5CC8',
      decimals: 6,
      symbol: 'USDC'
    },
    optimism: {
      contractAddress: '0x7F5c764cBc14f9669B88837ca1490cCa17c31607',
      decimals: 6,
      symbol: 'USDC'
    },
    avalanche: {
      contractAddress: '0xB97EF9Ef8734C71904D8002F8b6Bc66Dd9c48a6E',
      decimals: 6,
      symbol: 'USDC'
    },
    base: {
      contractAddress: '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913',
      decimals: 6,
      symbol: 'USDC'
    },
    // Non-EVM networks
    tron: {
      contractAddress: 'TEkxiTehnzSmSe2XqrBj4w32RUN966rdz8',
      decimals: 6,
      symbol: 'USDC'
    },
    algorand: {
      assetId: 31566704,
      decimals: 6,
      symbol: 'USDC'
    },
    solana: {
      contractAddress: 'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v',
      decimals: 6,
      symbol: 'USDC'
    }
  }
};

// Network to wallet mapping (updated for wallet-connect-core)
export const NETWORK_WALLET_MAP = {
  [NETWORK_TYPES.ETHEREUM]: [WALLET_TYPES.METAMASK, WALLET_TYPES.COINBASE_WALLET, WALLET_TYPES.WALLETCONNECT, WALLET_TYPES.TRUST_WALLET],
  [NETWORK_TYPES.POLYGON]: [WALLET_TYPES.METAMASK, WALLET_TYPES.COINBASE_WALLET, WALLET_TYPES.WALLETCONNECT, WALLET_TYPES.TRUST_WALLET],
  [NETWORK_TYPES.BSC]: [WALLET_TYPES.METAMASK, WALLET_TYPES.COINBASE_WALLET, WALLET_TYPES.WALLETCONNECT, WALLET_TYPES.TRUST_WALLET],
  [NETWORK_TYPES.ARBITRUM]: [WALLET_TYPES.METAMASK, WALLET_TYPES.COINBASE_WALLET, WALLET_TYPES.WALLETCONNECT, WALLET_TYPES.TRUST_WALLET],
  [NETWORK_TYPES.OPTIMISM]: [WALLET_TYPES.METAMASK, WALLET_TYPES.COINBASE_WALLET, WALLET_TYPES.WALLETCONNECT, WALLET_TYPES.TRUST_WALLET],
  [NETWORK_TYPES.AVALANCHE]: [WALLET_TYPES.METAMASK, WALLET_TYPES.COINBASE_WALLET, WALLET_TYPES.WALLETCONNECT, WALLET_TYPES.TRUST_WALLET],
  [NETWORK_TYPES.BASE]: [WALLET_TYPES.METAMASK, WALLET_TYPES.COINBASE_WALLET, WALLET_TYPES.WALLETCONNECT, WALLET_TYPES.TRUST_WALLET],
  // Non-EVM networks (legacy support)
  [NETWORK_TYPES.TRON]: [WALLET_TYPES.TRONLINK, WALLET_TYPES.TRUST_WALLET],
  [NETWORK_TYPES.ALGORAND]: [WALLET_TYPES.LUTE],
  [NETWORK_TYPES.SOLANA]: [WALLET_TYPES.PHANTOM, WALLET_TYPES.TRUST_WALLET]
};

// Global wallet configuration
let walletConfig = null;

/**
 * Initialize wallet core configuration
 */
export const initializeWalletCore = (config = {}) => {
  const defaultConfig = {
    appName: 'Coinley Checkout',
    appDescription: 'Secure cryptocurrency payment processing',
    appUrl: 'https://coinley.com',
    appIcon: 'https://coinley.com/icon.png',
    theme: 'auto',
    enableAnalytics: false,
    // Use only supported EVM networks for wallet-connect-core
    chains: ['ethereum', 'polygon', 'arbitrum', 'optimism', 'avalanche', 'base'],
    ...config
  };

  try {
    walletConfig = createQuickConfig(defaultConfig);
    return walletConfig;
  } catch (error) {
    console.error('❌ Failed to initialize wallet core:', error);
    throw error;
  }
};

/**
 * Get the current wallet configuration
 */
export const getWalletConfig = () => {
  if (!walletConfig) {
    console.warn('⚠️ Wallet core not initialized, creating default config');
    return initializeWalletCore();
  }
  return walletConfig;
};

/**
 * Modern wallet connection using @coinley/wallet-connect-core
 */
export const connectWallet = async (walletType, network) => {
  
  try {
    // For EVM networks, use wallet-connect-core
    if (isEvmNetwork(network)) {
      return await connectEvmWallet(walletType, network);
    }
    
    // For non-EVM networks, fall back to legacy connection
    return await connectLegacyWallet(walletType, network);
    
  } catch (error) {
    console.error(`❌ Wallet connection failed for ${walletType}:`, error);
    throw error;
  }
};

/**
 * Connect to EVM wallets using wallet-connect-core
 */
const connectEvmWallet = async (walletType, network) => {
  // This function would be called within a React component context
  // For now, we'll return a placeholder that indicates the need for React context
  throw new Error('EVM wallet connection requires React component context. Use useWalletConnect hook instead.');
};

/**
 * Legacy wallet connection for non-EVM networks
 */
const connectLegacyWallet = async (walletType, network) => {
  // Import legacy connection functions
  const { connectWallet: legacyConnect } = await import('./walletService.js');
  return await legacyConnect(walletType, network);
};

/**
 * Check if network is EVM-compatible
 */
export const isEvmNetwork = (network) => {
  return [
    NETWORK_TYPES.ETHEREUM,
    NETWORK_TYPES.POLYGON,
    NETWORK_TYPES.BSC,
    NETWORK_TYPES.ARBITRUM,
    NETWORK_TYPES.OPTIMISM,
    NETWORK_TYPES.AVALANCHE,
    NETWORK_TYPES.BASE
  ].includes(network);
};

/**
 * Send transaction using wallet-connect-core (for EVM) or legacy service
 */
export const sendTransaction = async (walletConnection, transactionData) => {
  const { to, amount, currency, network, tokenAddress } = transactionData;
  
  
  try {
    if (isEvmNetwork(network)) {
      // This would require React context - throw helpful error
      throw new Error('EVM transactions require React component context. Use useWalletTransaction hook instead.');
    }
    
    // For non-EVM networks, use legacy service
    const { sendTransaction: legacySend } = await import('./walletService.js');
    return await legacySend(walletConnection, transactionData);
    
  } catch (error) {
    console.error('❌ Transaction failed:', error);
    throw error;
  }
};

/**
 * Get token balance using wallet-connect-core or legacy service
 */
export const getTokenBalance = async (tokenAddress, network, walletAddress) => {
  try {
    if (isEvmNetwork(network)) {
      // This would require React context - return null for now
      console.warn('⚠️ EVM token balance requires React component context. Use useTokenBalance hook instead.');
      return null;
    }
    
    // For non-EVM networks, use legacy service
    const { getTokenBalance: legacyBalance } = await import('./web3.js');
    return await legacyBalance(tokenAddress, network, walletAddress);
    
  } catch (error) {
    console.error('❌ Failed to get token balance:', error);
    return null;
  }
};

/**
 * Detect available wallets
 */
export const detectWallets = async () => {
  try {
    // Detect EVM wallets
    const evmWallets = {};
    
    // Check for browser wallets
    if (typeof window !== 'undefined') {
      evmWallets[WALLET_TYPES.METAMASK] = !!window.ethereum?.isMetaMask;
      evmWallets[WALLET_TYPES.COINBASE_WALLET] = !!window.ethereum?.isCoinbaseWallet;
      evmWallets[WALLET_TYPES.TRUST_WALLET] = !!window.ethereum?.isTrustWallet;
    }
    
    // For non-EVM wallets, use legacy detection
    const { detectWallets: legacyDetect } = await import('./walletService.js');
    const legacyWallets = await legacyDetect();
    
    return {
      ...evmWallets,
      ...legacyWallets
    };
    
  } catch (error) {
    console.error('Error detecting wallets:', error);
    return {
      [WALLET_TYPES.METAMASK]: false,
      [WALLET_TYPES.COINBASE_WALLET]: false,
      [WALLET_TYPES.TRUST_WALLET]: false,
      [WALLET_TYPES.PHANTOM]: false,
      [WALLET_TYPES.TRONLINK]: false,
      [WALLET_TYPES.LUTE]: false
    };
  }
};

/**
 * Get supported wallets for a network
 */
export const getSupportedWalletsForNetwork = (network) => {
  return NETWORK_WALLET_MAP[network] || [];
};

/**
 * Get wallet install URL
 */
export const getWalletInstallUrl = (walletType) => {
  const urls = {
    [WALLET_TYPES.METAMASK]: 'https://metamask.io/download/',
    [WALLET_TYPES.COINBASE_WALLET]: 'https://www.coinbase.com/wallet/downloads',
    [WALLET_TYPES.TRUST_WALLET]: 'https://trustwallet.com/download/',
    [WALLET_TYPES.PHANTOM]: 'https://phantom.app/download',
    [WALLET_TYPES.TRONLINK]: 'https://www.tronlink.org/download/',
    [WALLET_TYPES.LUTE]: 'https://lute.app/download/'
  };
  return urls[walletType] || '';
};

/**
 * Disconnect wallet
 */
export const disconnectWallet = async (walletConnection) => {
  try {
    if (walletConnection.network && isEvmNetwork(walletConnection.network)) {
      return { success: true };
    }
    
    // For non-EVM wallets, use legacy disconnection
    const { disconnectWallet: legacyDisconnect } = await import('./walletService.js');
    return await legacyDisconnect(walletConnection);
    
  } catch (error) {
    console.error('❌ Failed to disconnect wallet:', error);
    throw error;
  }
};

/**
 * Switch network (EVM networks only)
 */
export const switchNetwork = async (targetNetwork) => {
  if (!isEvmNetwork(targetNetwork)) {
    throw new Error(`Network switching not supported for ${targetNetwork}`);
  }
  
  // This would require React context
  throw new Error('Network switching requires React component context. Use useNetworkSwitch hook instead.');
};

// Export React components and hooks for use in components
export {
  CoinleyWalletProvider,
  ConnectButton,
  WalletModal,
  useWallet,
  useWalletConnect,
  useWalletSign,
  useWalletSignTypedData,
  useNetworkSwitch,
  useTokenBalance,
  useWalletTransaction,
  useWalletModal,
  useWalletDetection
};

// Debug helper
export const debugWalletEnvironment = () => {
  return {
    message: 'Wallet debugging for modern wallet-connect-core integration',
    evmNetworksSupported: Object.keys(NETWORK_CONFIG).filter(isEvmNetwork),
    legacyNetworksSupported: Object.keys(NETWORK_CONFIG).filter(n => !isEvmNetwork(n)),
    availableWallets: Object.keys(WALLET_TYPES),
    walletCoreInitialized: !!walletConfig
  };
};